/**
 * Progressive "Load more" for paginated grids.
 *
 * The server always renders real numbered pagination links. This element only
 * hides them once it has confirmed it can do the job itself, so a failure — or
 * no JavaScript at all — leaves working, crawlable pagination behind.
 *
 * Appends to the grid rather than replacing it, and moves focus to the first
 * newly added card so keyboard and screen reader users are not dropped back at
 * the top of the page.
 */
(function () {
  'use strict';

  if (!window.Theme) return;

  class LoadMore extends window.Theme.ThemeElement {
    init() {
      this.button = this.querySelector('[data-load-more-button]');
      this.nextUrl = this.dataset.nextUrl;
      this.targetSelector = this.dataset.target;
      if (!this.button || !this.nextUrl) return;

      // Only now is it safe to hide the numbered links.
      const list = this.closest('.pagination')?.querySelector('.pagination__list');
      if (list) list.hidden = true;

      this.on(this.button, 'click', () => this.load());
    }

    async load() {
      if (this.loading) return;
      this.loading = true;
      this.button.setAttribute('aria-busy', 'true');

      try {
        const doc = await window.Theme.fetchDocument(this.nextUrl);
        const grid = document.querySelector(this.targetSelector);
        const incoming = doc.querySelector(this.targetSelector);
        if (!grid || !incoming) throw new Error('Grid not found');

        const added = Array.from(incoming.children);
        added.forEach((node) => grid.appendChild(node));

        // Advance, or retire the button when there is nothing left.
        const nextPagination = doc.querySelector('load-more');
        if (nextPagination && nextPagination.dataset.nextUrl) {
          this.nextUrl = nextPagination.dataset.nextUrl;
          this.dataset.nextUrl = this.nextUrl;
        } else {
          this.remove();
        }

        window.Theme.announce(
          this.dataset.announcement || `${grid.children.length} products`
        );

        const firstNew = added[0];
        if (firstNew) {
          const focusable = firstNew.querySelector('a, button') || firstNew;
          focusable.setAttribute('tabindex', '-1');
          focusable.focus({ preventScroll: true });
        }
      } catch (error) {
        // Restore the real links so the customer is never stranded.
        const list = this.closest('.pagination')?.querySelector('.pagination__list');
        if (list) list.hidden = false;
        this.remove();
      } finally {
        this.loading = false;
        if (this.isConnected) this.button.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('load-more')) customElements.define('load-more', LoadMore);
})();
